import axios from 'axios';

import store from '../store';
import { pickCredentials } from '../redux/consumer/consumerSession';

export const AUTH_SHAPE = {
  NONE: 'none',
  FLAT: 'flat',
  PROJECT_REQUEST: 'projectRequest',
  LOGIN_REQUEST: 'loginRequest',
  QUERY: 'query',
};

const isFormData = (data) => typeof FormData !== 'undefined' && data instanceof FormData;

const hasRequestPayload = (data) => {
  if (data == null) {
    return false;
  }

  if (isFormData(data)) {
    return true;
  }

  if (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0) {
    return false;
  }

  return true;
};

const resolveCredentials = (config) => {
  if (config.consumerCredentials) {
    return pickCredentials(config.consumerCredentials);
  }

  return pickCredentials(store.getState().consumer?.session);
};

const applyAuthShape = (config) => {
  const authShape = config.authShape ?? AUTH_SHAPE.NONE;

  if (authShape === AUTH_SHAPE.NONE) {
    return config;
  }

  const credentials = resolveCredentials(config);

  if (!credentials) {
    throw new Error('Consumer credentials are required for this request');
  }

  if (authShape === AUTH_SHAPE.FLAT) {
    if (hasRequestPayload(config.data)) {
      throw new Error('FLAT authShape replaces the request body with credentials; do not pass data');
    }

    config.data = { ...credentials };
    return config;
  }

  if (authShape === AUTH_SHAPE.PROJECT_REQUEST) {
    if (isFormData(config.data)) {
      throw new Error('PROJECT_REQUEST cannot wrap FormData; use AUTH_SHAPE.QUERY for multipart uploads');
    }

    config.data = {
      projectRequest: { ...credentials },
      request: config.data,
    };
    return config;
  }

  if (authShape === AUTH_SHAPE.LOGIN_REQUEST) {
    if (isFormData(config.data)) {
      throw new Error('LOGIN_REQUEST cannot wrap FormData; use AUTH_SHAPE.QUERY for multipart uploads');
    }

    config.data = {
      loginRequest: { ...credentials },
      calculationsRequest: config.data,
    };
    return config;
  }

  if (authShape === AUTH_SHAPE.QUERY) {
    config.params = {
      ...config.params,
      ...credentials,
    };
    return config;
  }

  throw new Error(`Unknown consumer authShape: ${authShape}`);
};

const consumerApi = axios.create();

consumerApi.interceptors.request.use(
  (config) => applyAuthShape(config),
  (error) => Promise.reject(error),
);

export default consumerApi;
